import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import type { Farm, Zone } from '../../types';
import {
  Box,
  Button,
  Paper,
  Card,
  CardActions,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { Add, Delete, Edit, HomeWork } from '@mui/icons-material';

// Add debug properties to window for TypeScript
declare global {
  interface Window {
    _debugFarmsData?: any;
    _debugFarmsWithZones?: any;
  }
}

// UI-only fields for inline editing
export type FarmWithUI = Farm & {
  zones: (Zone & { editingName?: string })[];
  newZoneName?: string;
};

const FarmList: React.FC = () => {
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [farms, setFarms] = useState<FarmWithUI[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFarm, setEditingFarm] = useState<Farm | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Farm | null>(null);
  const [snackbar, setSnackbar] = useState<{ 
    open: boolean; 
    message: string; 
    severity: 'success' | 'error' | 'info' | 'warning' 
  }>({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });
  const [formData, setFormData] = useState({
    name: '',
  });

  // Function to fetch farms and zones
  const fetchFarms = async () => {
    console.log('Current user in FarmList:', user);
    if (!user) {
      const errorMsg = 'No user found. User might not be authenticated.';
      console.error(errorMsg);
      setError('Please log in to view farms.');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      console.log('Fetching farms for user:', user.id);
      
      // Fetch farms for the current user
      const { data: farmsData, error: farmsError } = await api.farms.getAll();
      console.log('Farms data:', farmsData);
      
      // Debug: show farmsData in UI
      window._debugFarmsData = farmsData;
      
      if (farmsError) {
        throw new Error(farmsError);
      }
      if (!Array.isArray(farmsData)) {
        throw new Error('Expected farmsData to be an array');
      }

      // Fetch zones for each farm
      const farmsWithZones = await Promise.all(
        (farmsData || []).map(async (farm: Farm) => {
          try {
            const { data: zonesData, error: zonesError } = await api.zones.getAll(farm.id);
            if (zonesError) {
              console.error('Zones fetch error for farm', farm.id, zonesError);
            }
            console.log('Zones fetch for farm', farm.id, 'zonesData:', zonesData);
            return {
              ...farm,
              zones: (zonesData || []).map((z: Zone & { editingName?: string }) => ({ ...z, editingName: undefined })), 
              newZoneName: ''
            };
          } catch (zoneErr) {
            console.error('Zones fetch exception for farm', farm.id, zoneErr);
            return {
              ...farm,
              zones: [],
              newZoneName: ''
            };
          }
        })
      );

      // Debug: show farmsWithZones in UI
      window._debugFarmsWithZones = farmsWithZones;

      setFarms(farmsWithZones);
      return farmsWithZones;
    } catch (error) {
      console.error('Error loading farms:', error);
      setError('Failed to load farms. Please try again.');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Load farms and zones on component mount or when user changes
  useEffect(() => {
    fetchFarms();
  }, [user]);

  const handleOpenDialog = (farm: Farm | null = null) => {
    if (farm) {
      setEditingFarm(farm);
      setFormData({ name: farm.name });
    } else {
      setEditingFarm(null);
      setFormData({ name: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingFarm(null);
    setFormData({ name: '' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      if (editingFarm) {
        // Update existing farm
        console.log('Updating farm:', editingFarm.id, formData.name);
        const result = await api.farms.update(editingFarm.id, formData.name);
        console.log('Update farm result:', result);
        
        // Check if there's an error in the response
        if (result.error) {
          throw new Error(result.error);
        }
        
        // Update the farms state with the new name
        setFarms(prev =>
          prev.map(farm =>
            farm.id === editingFarm.id ? { ...farm, name: formData.name } : farm
          )
        );
        setSnackbar({ open: true, message: 'Farm updated successfully', severity: 'success' });
        handleCloseDialog();
      } else {
        // First close the dialog to improve perceived performance
        handleCloseDialog();
        
        // Show loading state
        setLoading(true);
        
        // Create new farm
        console.log('Creating new farm:', formData.name);
        const { data, error } = await api.farms.create(formData.name);
        console.log('Create farm result:', data, error);

        if (error) {
          throw new Error(error);
        }

        // Show success message
        setSnackbar({ open: true, message: 'Farm created successfully', severity: 'success' });
        
        // Immediately refresh the farm list
        console.log('Refreshing farm list after creating new farm');
        await fetchFarms();
      }
    } catch (error) {
      console.error('Error saving farm:', error);
      setSnackbar({ open: true, message: `Failed to save farm: ${error instanceof Error ? error.message : 'Unknown error'}`, severity: 'error' });
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;

    try {
      // Delete the farm (mock API will handle related zones)
      const { error } = await api.farms.delete(confirmDelete.id);

      if (error) throw error;

      setFarms(prev => prev.filter(farm => farm.id !== confirmDelete.id));
      setSnackbar({ open: true, message: 'Farm deleted successfully', severity: 'success' });
    } catch (error) {
      console.error('Error deleting farm:', error);
      setSnackbar({ open: true, message: 'Failed to delete farm', severity: 'error' });
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const navigateToFarm = (farmId: string) => {
    navigate(`/farms/${farmId}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <HomeWork sx={{ mr: 1, verticalAlign: 'middle' }} />
          Farms
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Farm
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {farms.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No farms found
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Click the "Add Farm" button to create your first farm.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {farms.map((farm) => (
            <Grid item xs={12} md={6} lg={4} key={farm.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>{farm.name}</Typography>
                  <Typography variant="subtitle2" color="text.secondary">Zones</Typography>

                  <Box sx={{ pl: 1 }}>
                    {farm.zones && farm.zones.length > 0 ? (
                      farm.zones.map((zone: Zone & { editingName?: string }) => (
                        <Box key={zone.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          {('editingName' in zone) && zone.editingName !== undefined ? (
                            <TextField
                              size="small"
                              value={zone.editingName}
                              onChange={e => {
                                setFarms(prev => prev.map(f =>
                                  f.id === farm.id ? {
                                    ...f,
                                    zones: f.zones.map((z: Zone & { editingName?: string }) =>
                                      z.id === zone.id ? { ...z, editingName: e.target.value } : z
                                    )
                                  } : f
                                ));
                              }}
                              onBlur={async () => {
                                // Save zone name
                                const newName = zone.editingName?.trim();
                                if (newName && newName !== zone.name) {
  await api.zones.update(zone.id, newName);
  const { data: zonesData, error: zonesError } = await api.zones.getAll(farm.id);
  setFarms(prev => prev.map(f =>
    f.id === farm.id ? {
      ...f,
      zones: (zonesData || []).map((z: Zone & { editingName?: string }) => ({ ...z, editingName: undefined }))
    } : f
  ));
  setSnackbar({ open: true, message: 'Zone updated', severity: 'success' });
                                } else {
                                  setFarms(prev => prev.map(f =>
                                    f.id === farm.id ? {
                                      ...f,
                                      zones: f.zones.map((z: Zone & { editingName?: string }) =>
                                        z.id === zone.id ? { ...z, editingName: undefined } : z
                                      )
                                    } : f
                                  ));
                                }
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                              }}
                              sx={{ mr: 1, width: 120 }}
                              autoFocus
                            />
                          ) : (
                            <Typography
                              sx={{ mr: 1, cursor: 'pointer', width: 120 }}
                              onClick={() => {
                                setFarms(prev => prev.map(f =>
                                  f.id === farm.id ? {
                                    ...f,
                                    zones: f.zones.map((z: Zone & { editingName?: string }) =>
                                      z.id === zone.id ? { ...z, editingName: z.name } : z
                                    )
                                  } : f
                                ));
                              }}
                            >
                              {zone.name}
                            </Typography>
                          )}
                          <IconButton
                            size="small"
                            color="error"
                            onClick={async () => {
                              await api.zones.delete(zone.id);
                              const { data: zonesData, error: zonesError } = await api.zones.getAll(farm.id);
                              setFarms(prev => prev.map(f =>
                                f.id === farm.id ? {
                                  ...f,
                                  zones: (zonesData || []).map((z: Zone & { editingName?: string }) => ({ ...z, editingName: undefined }))
                                } : f
                              ));
                              setSnackbar({ open: true, message: 'Zone deleted', severity: 'success' });
                            }}
                            aria-label="delete-zone"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      ))
                    ) : (
                      <Typography color="text.secondary" sx={{ fontSize: 14, mb: 1 }}>
                        No zones
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Add zone"
                        value={farm.newZoneName || ''}
                        onChange={e => {
                          setFarms(prev => prev.map(f =>
                            f.id === farm.id ? { ...f, newZoneName: e.target.value } : f
                          ));
                        }}
                        onKeyDown={async e => {
                          if (e.key === 'Enter' && (farm.newZoneName || '').trim()) {
                            const { data, error } = await api.zones.create((farm.newZoneName || '').trim(), farm.id);
                            if (!error && data && data.id) {
  const { data: zonesData, error: zonesError } = await api.zones.getAll(farm.id);
                              setFarms(prev => prev.map(f =>
                                f.id === farm.id ? {
                                  ...f,
                                  zones: (zonesData || []).map((z: Zone & { editingName?: string }) => ({ ...z, editingName: undefined })), 
                                  newZoneName: ''
                                } : f
                              ));
                              setSnackbar({ open: true, message: 'Zone added', severity: 'success' });
                            } else {
                              setSnackbar({ open: true, message: 'Failed to add zone', severity: 'error' });
                            }
                          }
                        }}
                        sx={{ width: 120, mr: 1 }}
                      />
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={async () => {
                          if ((farm.newZoneName || '').trim()) {
                            const { data, error } = await api.zones.create((farm.newZoneName || '').trim(), farm.id);
                            if (!error && data && data.id) {
  const { data: zonesData, error: zonesError } = await api.zones.getAll(farm.id);
                              setFarms(prev => prev.map(f =>
                                f.id === farm.id ? {
                                  ...f,
                                  zones: (zonesData || []).map((z: Zone & { editingName?: string }) => ({ ...z, editingName: undefined })), 
                                  newZoneName: ''
                                } : f
                              ));
                              setSnackbar({ open: true, message: 'Zone added', severity: 'success' });
                            } else {
                              setSnackbar({ open: true, message: 'Failed to add zone', severity: 'error' });
                            }
                          }
                        }}
                        aria-label="add-zone"
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button size="small" color="primary" onClick={() => handleOpenDialog(farm)} startIcon={<Edit />}>
                    Edit
                  </Button>
                  <Button size="small" color="error" onClick={() => setConfirmDelete(farm)} startIcon={<Delete />}>
                    Delete
                  </Button>
                  
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Farm Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingFarm ? 'Edit Farm' : 'Add Farm'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Farm Name"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingFarm ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete "{confirmDelete?.name}"? This will also delete all zones
            and measurements associated with this farm. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FarmList;
